import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  GatewayConfig, GatewayLogRow, GatewayStatusInfo, GatewayTerminal,
  GatewayTxnStatus, PosInitiateResponse
} from '../../models/gateway.model';

const TERMINALS_KEY = 'tdt.gateway.terminals';
const SELECTED_TERMINAL_KEY = 'tdt.gateway.selectedTerminal';

@Injectable({ providedIn: 'root' })
export class GatewayService {
  private baseUrl = `${environment.apiUrl}/api/admin/gateway`;

  constructor(private http: HttpClient) {}

  getStatus(): Observable<GatewayStatusInfo> {
    return this.http.get<GatewayStatusInfo>(`${this.baseUrl}/status`);
  }

  /** Terminal list, cached in sessionStorage for the clerk's session (kubera behavior). */
  getTerminals(refresh = false): Observable<GatewayTerminal[]> {
    if (!refresh) {
      const cached = sessionStorage.getItem(TERMINALS_KEY);
      if (cached) {
        return of(JSON.parse(cached) as GatewayTerminal[]);
      }
    }
    return this.http.get<GatewayTerminal[]>(`${this.baseUrl}/terminals`).pipe(
      tap(list => sessionStorage.setItem(TERMINALS_KEY, JSON.stringify(list)))
    );
  }

  get selectedTerminal(): string | null {
    return sessionStorage.getItem(SELECTED_TERMINAL_KEY);
  }

  set selectedTerminal(deviceId: string | null) {
    if (deviceId) sessionStorage.setItem(SELECTED_TERMINAL_KEY, deviceId);
    else sessionStorage.removeItem(SELECTED_TERMINAL_KEY);
  }

  initiate(deviceId: string, accountId: number, amount: number,
           cart: { itemId: string; description: string; amount: number }[]):
      Observable<PosInitiateResponse> {
    return this.http.post<PosInitiateResponse>(`${this.baseUrl}/pos/initiate`,
        { deviceId, accountId, amount, cart });
  }

  txnStatus(transactionId: string): Observable<GatewayTxnStatus> {
    return this.http.get<GatewayTxnStatus>(`${this.baseUrl}/pos/status/${transactionId}`);
  }

  abort(transactionId: string, reason: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/pos/abort/${transactionId}`, { reason });
  }

  // ── Admin config ─────────────────────────────────────────────────────
  getConfigs(): Observable<GatewayConfig[]> {
    return this.http.get<GatewayConfig[]>(`${this.baseUrl}/config`);
  }

  updateConfig(config: GatewayConfig): Observable<GatewayConfig> {
    return this.http.put<GatewayConfig>(`${this.baseUrl}/config/${config.id}`, config);
  }

  activateConfig(id: number): Observable<GatewayConfig> {
    return this.http.post<GatewayConfig>(`${this.baseUrl}/config/${id}/activate`, {});
  }

  testConfig(id: number): Observable<{ result: string }> {
    return this.http.post<{ result: string }>(`${this.baseUrl}/config/${id}/test`, {});
  }

  getLogs(params: { from?: string; to?: string; transactionId?: string;
                    page?: number; size?: number }): Observable<{ content: GatewayLogRow[]; totalElements: number }> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') httpParams = httpParams.set(k, String(v));
    });
    return this.http.get<{ content: GatewayLogRow[]; totalElements: number }>(
        `${this.baseUrl}/logs`, { params: httpParams });
  }
}
